import { mintEntityId } from "@repo/domain";
import { useState } from "react";

export type SubmitOnce<I = string> = <T>(submit: (entityId: I) => T) => T | undefined;

/** The server refusing the id because another entity already holds it. */
function isIdentityConflict(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.message === "Identity already exists" &&
    (error as { data?: { code?: string } }).data?.code === "CONFLICT"
  );
}

/**
 * One submission per create surface. A second click (or Enter during the exit
 * animation) is dropped; a submit that fails reopens it, and the retry names
 * the same entity, so if the first did reach the server its create-uniqueness
 * check returns that record instead of making another. An identity conflict
 * means the id is spent on content the retry no longer matches, so the retry
 * names a new one.
 */
export function createSubmitOnce<I>(mint: () => I): SubmitOnce<I> {
  let entityId = mint();
  let state: "open" | "pending" | "done" = "open";
  const reopen = (error: unknown) => {
    state = "open";
    if (isIdentityConflict(error)) entityId = mint();
  };
  return (submit) => {
    if (state !== "open") return undefined;
    state = "pending";
    let result: ReturnType<typeof submit>;
    try {
      result = submit(entityId);
    } catch (error) {
      reopen(error);
      throw error;
    }
    if (result instanceof Promise) {
      result.then(() => {
        state = "done";
      }, reopen);
    } else {
      state = "done";
    }
    return result;
  };
}

/** `createSubmitOnce` for a dialog, minted once per mount. */
export function useSubmitOnce(): { once: SubmitOnce } {
  const [value] = useState(() => ({ once: createSubmitOnce(mintEntityId) }));
  return value;
}
